<template>
  <div class="login-page-wrapper">
    <!-- Main Login Card -->
    <div class="login-card">
      <!-- Top Gradient Badge -->
      <div class="badge-container">
        <div class="gradient-badge">
          <i class="fa-solid fa-lock badge-icon"></i>
        </div>
      </div>

      <!-- Header Titles -->
      <div class="login-header">
        <h1 class="login-title">Welcome back</h1>
        <p class="login-subtitle">Sign in to your account to continue</p>
      </div>

      <!-- Login Form -->
      <form @submit.prevent="handleLogin" class="login-form">
        <!-- Email Input -->
        <div class="form-group">
          <label for="loginEmail">Email</label>
          <div class="input-icon-wrapper">
            <i class="fa-regular fa-envelope input-leading-icon"></i>
            <input 
              id="loginEmail" 
              type="email" 
              v-model="form.email" 
              placeholder="Enter your email"
              required
              class="form-input"
            />
          </div>
        </div>

        <!-- Password Input -->
        <div class="form-group">
          <label for="loginPassword">Password</label>
          <div class="input-icon-wrapper">
            <i class="fa-solid fa-lock input-leading-icon"></i>
            <input 
              id="loginPassword" 
              :type="showPassword ? 'text' : 'password'" 
              v-model="form.password" 
              placeholder="Enter your password"
              required
              class="form-input"
            />
            <button 
              type="button" 
              @click="togglePasswordVisibility" 
              class="btn-toggle-visibility"
              tabindex="-1"
            >
              <i :class="['fa-regular', showPassword ? 'fa-eye-slash' : 'fa-eye']"></i>
            </button>
          </div>
        </div>

        <!-- Sign In Button -->
        <button type="submit" class="btn-sign-in" :disabled="isLoading">
          <span v-if="isLoading" class="spinner"></span>
          <span v-else>Sign in</span>
        </button>

        <router-link to="/forgot-password" class="forgot-link">Forgot password?</router-link>
      </form>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService';

export default {
  name: 'UserLogin',
  data() {
    return {
      form: {
        email: '',
        password: ''
      },
      showPassword: false,
      isLoading: false
    };
  },
  methods: {
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    async handleLogin() {
      this.isLoading = true;
      try {
        await AuthService.login({
          email: this.form.email,
          password: this.form.password
        });

        this.$toast.open({
          message: 'Signed in successfully.',
          type: 'success',
          position: 'top-right'
        });

        const redirect = this.$route.query.redirect || '/binance';
        this.$router.push(redirect);
      } catch (e) {
        this.$toast.open({
          message: 'Invalid email or password.',
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Main Page Wrapper: pastel gradient background and flex alignment */
.login-page-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 10% 20%, rgba(243, 235, 255, 1) 0%, rgba(225, 236, 255, 1) 90%);
  padding: 20px;
  box-sizing: border-box;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  text-align: left;
}

/* Rounded Elegant Card */
.login-card {
  background: #ffffff;
  border-radius: 32px;
  padding: 48px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 40px rgba(124, 58, 237, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Badge holding Lock Icon */
.badge-container {
  margin-bottom: 24px;
}

.gradient-badge {
  width: 58px;
  height: 58px;
  border-radius: 16px;
  background: linear-gradient(135deg, #6366f1 0%, #d946ef 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 22px;
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.25);
}

/* Header text styles */
.login-header {
  text-align: center;
  margin-bottom: 36px;
}

.login-title {
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
}

.login-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* Form inputs & groups */
.login-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.input-icon-wrapper {
  position: relative;
  width: 100%;
}

.input-leading-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
}

.form-input {
  width: 100%;
  padding: 14px 16px 14px 44px;
  font-size: 14px;
  border: 1px solid #e9d5ff; /* Soft purple borders */
  border-radius: 14px;
  outline: none;
  background: #fdfcff;
  color: #0f172a;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

.form-input:focus {
  border-color: #a855f7;
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(168, 85, 247, 0.08);
}

.form-input::placeholder {
  color: #cbd5e1;
}

/* Toggle eye button for password visibility */
.btn-toggle-visibility {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  font-size: 16px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.btn-toggle-visibility:hover {
  color: #64748b;
}

/* Sign In Submit Button */
.btn-sign-in {
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(90deg, #6366f1 0%, #d946ef 100%);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.2);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-sign-in:hover:not(:disabled) {
  opacity: 0.95;
  transform: translateY(-1px);
  box-shadow: 0 10px 28px rgba(99, 102, 241, 0.3);
}

.btn-sign-in:active:not(:disabled) {
  transform: translateY(0);
}

.btn-sign-in:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.forgot-link {
  align-self: center;
  color: #6366f1;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
}

/* Spinner for Loading State */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Global Dark Mode Support overrides */
:global(.dark-mode) .login-page-wrapper {
  background: radial-gradient(circle at 10% 20%, #0d0e12 0%, #1e1b29 90%);
}

:global(.dark-mode) .login-card {
  background: #181a20;
  border-color: rgba(255, 255, 255, 0.05);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 8px 16px rgba(0, 0, 0, 0.2);
}

:global(.dark-mode) .login-title {
  color: #f1f5f9;
}

:global(.dark-mode) .login-subtitle {
  color: #94a3b8;
}

:global(.dark-mode) .form-group label {
  color: #cbd5e1;
}

:global(.dark-mode) .form-input {
  background: #232730;
  border-color: #475569;
  color: #f1f5f9;
}

:global(.dark-mode) .form-input:focus {
  border-color: #a78bfa;
  background: #1e222a;
  box-shadow: 0 0 0 4px rgba(167, 139, 250, 0.1);
}

:global(.dark-mode) .form-input::placeholder {
  color: #64748b;
}

:global(.dark-mode) .btn-toggle-visibility {
  color: #64748b;
}

:global(.dark-mode) .btn-toggle-visibility:hover {
  color: #94a3b8;
}
</style>
